import { useAuth } from '@contexts/auth-provider';
import { useMsalAuth } from '@contexts/msal-provider';
import { APP_ROUTES } from '@routes/routes.constants';
import { AppLoader } from '@shared/components/app-loader/AppLoader';
import { AuthErrorScreen } from '@shared/components/error-pages/auth-error/AuthErrorScreen';
import * as React from 'react';
import { Navigate } from 'react-router-dom';

/**
 * Trampoline page for Microsoft My Apps B2E SSO.
 *
 * Three states:
 * 1. Loading → show AppLoader
 * 2. Already authenticated → redirect to app-initializer
 * 3. Not authenticated → trigger B2E login (one-time via ref guard)
 *
 * After login('b2e'), MSAL redirects to Microsoft, then back to /app-initializer.
 * The user never returns to this page.
 */
const InternalLoginPage = React.memo((): React.ReactElement => {
  const { isAuthenticated, isLoading } = useAuth();
  const { login } = useMsalAuth();
  const loginTriggered = React.useRef(false);
  const [hasLoginFailed, setHasLoginFailed] = React.useState(false);

  const startLogin = React.useCallback((): void => {
    loginTriggered.current = true;
    setHasLoginFailed(false);
    void login('b2e').catch((error: unknown) => {
      console.error('Internal login failed:', error);
      setHasLoginFailed(true);
    });
  }, [login]);

  const retryLogin = React.useCallback((): void => {
    loginTriggered.current = false;
    startLogin();
  }, [startLogin]);

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated && !loginTriggered.current) {
      startLogin();
    }
  }, [isLoading, isAuthenticated, startLogin]);

  if (isLoading) {
    return <AppLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to={APP_ROUTES.APP_INITIALIZER} replace />;
  }

  if (hasLoginFailed) {
    return <AuthErrorScreen onRetry={retryLogin} />;
  }

  return <AppLoader />;
});

InternalLoginPage.displayName = 'InternalLoginPage';

export { InternalLoginPage };
